import React, { useState, useRef, useEffect } from 'react';
import SpeedMenu from './SpeedMenu';
import TransitionPicker from './TransitionPicker';

interface SegmentToolbarProps {
  hasSelection: boolean;
  segmentNumber: number | null;
  segmentCount: number;
  trimActive: boolean;
  onTrimToggle: () => void;
  onSplit: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  /** False at the ends of the timeline, where there is nowhere to move to. */
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onMerge: () => void;
  /** True only when exactly two adjacent segments are selected. */
  canMerge: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  canDelete: boolean;
  muted: boolean;
  onMuteToggle: () => void;
  volume: number;
  onVolumeChange: (v: number) => void;
  speed: number;
  onSpeedChange: (s: number) => void;
  transition: string;
  onTransitionChange: (t: string) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
}

const SegmentToolbar: React.FC<SegmentToolbarProps> = ({
  hasSelection, segmentNumber, segmentCount,
  trimActive, onTrimToggle,
  onSplit, onMoveLeft, onMoveRight, canMoveLeft, canMoveRight,
  onMerge, canMerge, onDuplicate, onDelete, canDelete,
  muted, onMuteToggle,
  volume, onVolumeChange,
  speed, onSpeedChange,
  transition, onTransitionChange,
  zoom, onZoomChange,
}) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click — same behaviour as the right rail toolbar.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Selection is what every control here acts on, so losing it closes any open menu.
  useEffect(() => {
    if (!hasSelection) setOpenMenu(null);
  }, [hasSelection]);

  const toggleMenu = (name: string) => setOpenMenu(prev => prev === name ? null : name);

  return (
    <div className="segment-toolbar" ref={barRef}>
      <div className="segment-toolbar-left">
        <button
          className={`tt-btn ${trimActive ? 'tt-btn--active' : ''}`}
          onClick={onTrimToggle}
          title="Show the trim handles on the timeline (T)"
        >
          <span className="tt-icon">⇹</span><span className="tt-label">Trim</span>
        </button>
        <button
          className="tt-btn" onClick={onSplit} disabled={!hasSelection}
          title="Split the selected segment at the playhead"
        >
          <span className="tt-icon">✂</span><span className="tt-label">Split</span>
        </button>
        <button
          className="tt-btn" onClick={onMoveLeft} disabled={!canMoveLeft}
          title={canMoveLeft ? 'Move this segment one place earlier' : 'Already first'}
        >
          <span className="tt-icon">◀</span><span className="tt-label">Left</span>
        </button>
        <button
          className="tt-btn" onClick={onMoveRight} disabled={!canMoveRight}
          title={canMoveRight ? 'Move this segment one place later' : 'Already last'}
        >
          <span className="tt-icon">▶</span><span className="tt-label">Right</span>
        </button>

        <button
          className="tt-btn" onClick={onMerge} disabled={!canMerge}
          title="Merge the two selected segments into one — Ctrl/Shift-click a neighbour to pick the second"
        >
          <span className="tt-icon">⋈</span><span className="tt-label">Merge</span>
        </button>
        <button
          className="tt-btn" onClick={onDuplicate} disabled={!hasSelection}
          title="Insert a copy of the selected segment after it"
        >
          <span className="tt-icon">⧉</span><span className="tt-label">Duplicate</span>
        </button>
        <button
          className="tt-btn" onClick={onDelete} disabled={!hasSelection || !canDelete}
          title={canDelete ? 'Delete the selected segment (Del)' : 'A clip must keep at least one segment'}
        >
          <span className="tt-icon">🗑</span><span className="tt-label">Delete</span>
        </button>

        <div className="tt-divider" />

        <button
          className={`tt-btn ${muted ? 'tt-btn--active' : ''}`}
          onClick={onMuteToggle} disabled={!hasSelection}
          title="Silence this segment only"
        >
          <span className="tt-icon">{muted ? '🔇' : '🔊'}</span>
          <span className="tt-label">{muted ? 'Unmute' : 'Mute'}</span>
        </button>
        <div className="toolbar-dropdown-wrap">
          <button
            className="tt-btn" onClick={() => toggleMenu('volume')} disabled={!hasSelection}
            title="Set how loud this segment plays"
          >
            <span className="tt-icon">🎚</span>
            <span className="tt-label">Volume ▾</span>
          </button>
          {openMenu === 'volume' && (
            <div className="dropdown-menu segment-volume-menu">
              <div className="dropdown-header">Segment Volume</div>
              <div className="segment-volume-row">
                <input
                  type="range" min={0} max={200} step={5}
                  value={Math.round(volume * 100)}
                  onChange={e => onVolumeChange(Number(e.target.value) / 100)}
                />
                <span className="segment-volume-val">{Math.round(volume * 100)}%</span>
              </div>
            </div>
          )}
        </div>

        <div className="tt-divider" />

        <div className="toolbar-dropdown-wrap">
          <button
            className="tt-btn" onClick={() => toggleMenu('speed')} disabled={!hasSelection}
            title="Playback rate for this segment"
          >
            <span className="tt-icon">⏱️</span>
            <span className="tt-label">{speed}× ▾</span>
          </button>
          {openMenu === 'speed' && (
            <SpeedMenu currentSpeed={speed} onSelect={onSpeedChange} onClose={() => setOpenMenu(null)} />
          )}
        </div>
        <div className="toolbar-dropdown-wrap">
          <button
            className="tt-btn" onClick={() => toggleMenu('transition')} disabled={!hasSelection}
            title="Transition entering this segment"
          >
            <span className="tt-icon">➡️</span>
            <span className="tt-label">Transitions ▾</span>
          </button>
          {openMenu === 'transition' && (
            <TransitionPicker
              currentTransition={transition}
              onSelect={onTransitionChange}
              onClose={() => setOpenMenu(null)}
            />
          )}
        </div>
      </div>

      <div className="segment-toolbar-right">
        <span className="segment-toolbar-target">
          {hasSelection && segmentNumber !== null
            ? `Segment ${segmentNumber} of ${segmentCount}`
            : 'No segment selected'}
        </span>
        <div className="tt-divider" />
        <div className="tt-zoom">
          <button
            className="tt-btn tt-btn--sm"
            onClick={() => onZoomChange(Math.max(1, zoom - 1))}
            title="Zoom Out (Ctrl+-)"
          >
            <span className="tt-icon">−</span>
          </button>
          <input
            type="range"
            className="tt-zoom-slider"
            min={1}
            max={10}
            step={0.5}
            value={zoom}
            onChange={e => onZoomChange(Number(e.target.value))}
            title={`Zoom: ${zoom}x`}
          />
          <button
            className="tt-btn tt-btn--sm"
            onClick={() => onZoomChange(Math.min(10, zoom + 1))}
            title="Zoom In (Ctrl++)"
          >
            <span className="tt-icon">+</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SegmentToolbar;
